import React, { useEffect } from 'react'
import {
  Button,
  Checkbox,
  Classes,
  FormGroup,
  HTMLSelect,
  InputGroup,
  Label,
  TextArea,
} from '@blueprintjs/core'
import { Box, ThemedDialog, useToggle, Text } from 'ui-components'
import { TaskT } from 'ui-io/task-io/task-t'
import { taskBlockMutation } from 'ui-io/queries/tasks/task-block-mutation'
import { useMutation, useQuery, useQueryClient } from 'react-query'
// import { TASKS_QUERY_FILTER } from 'ui-io/queries/tasks/tasks-query'
import { delay } from 'ui-lib/delay'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import * as t from 'io-ts'
// import scrapeConfigurationByIdQuery from 'ui-io/queries/scrape-configuration-by-id-query'
// import { updateScheduleMutation } from 'ui-io/job-run-io/update-schedule'
import assetsSettingsQuery from 'ui-io/queries/assets/assets-settings-query'

import toasts from '../../utils/toasts'
import { formatLocation } from '../../routes'
import useVerification from '../use-verification'
import { TASKS_QUERY_FILTER } from 'ui-io/queries/tasks/task-query'
import { TASKS_SEARCH_QUERY } from 'ui-io/queries/tasks/tasks-query'

const BlockTaskDialog = ({
  task,
  isModalOpen,
  toggleModal,
}: {
  task: TaskT
  isModalOpen: boolean
  toggleModal: () => void
}) => {
  const history = useNavigate()
  const [blockReason, setBlockReason] = useState<string | undefined>(undefined)
  const [blockReasons, setBlockReasons] = useState<string[]>([])
  const [pauseConfig, togglePauseConfig] = useToggle(false)
  const [createRadar, setCreateRadar] = useState(false)
  const queryClient = useQueryClient()
  const { validator, isValidated, handleOnReset } = useVerification({
    toValidate: task.id,
  })
  const listOfReasonsNeedsRadar = [
    'Parsec rate limit',
    'Parsec headless',
    'Robots not allowed',
  ]
  // const connection = useQuery(
  //   scrapeConfigurationByIdQuery({
  //     scrapeConfigurationId: task._raw.configuration.uuid,
  //   }),
  // )
  const blockReasonsConfigConnection = useQuery(
    assetsSettingsQuery(
      '/settings/task',
      'task-block-reasons-config',
    ),
  )
  // useEffect(() => {
  //   if (
  //     blockReasonsConfigConnection.data?.settings?.keys[
  //       'task-block-reasons-config'
  //     ]?.config
  //   ) {
  //     setBlockReasons(
  //       blockReasonsConfigConnection.data.settings?.keys[
  //         'task-block-reasons-config'
  //       ]?.config,
  //     )
  //   }
  // }, [blockReasonsConfigConnection.data, setBlockReasons])

  const isPaused = false
  // (connection.isSuccess && connection.data.jobSchedule?.paused) ?? false
  const configurationId = task.configUuid
  const mutation = useMutation(taskBlockMutation, {
    onSuccess: async (result) => {
      if (t.type({ failure: t.string }).is(result[0])) {
        toasts.error(result[0].failure)
      } else {
        toasts.success('Successfully blocked the task')
      }
      await delay(500)
      queryClient.refetchQueries([TASKS_QUERY_FILTER])

      history(formatLocation.taskInbox({}))
    },
    onError: (error) => {
      toasts.error(`Error: ${error}`)
    },
    onSettled: () => {
      toggleModal()
    },
  })

  const [fields, setFields] = React.useState({
    radar: '',
    comment: '',
    blockedUrls: '',
  })

  const isLoading = mutation.isLoading

  const handleOnFieldChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setFields({ ...fields, [event.target.name]: event.target.value })
  }

  const handleOnClosing = () => {
    handleOnReset()
    setFields({ radar: '', comment: '', blockedUrls: '' })
  }

  // const scheduleMutation = useMutation(updateScheduleMutation, {
  //   onSuccess: () => {
  //     toasts.success(`Successfully paused schedule.`)
  //   },
  //   onError: (err) => {
  //     toasts.error(
  //       err instanceof Error ? err.message : 'Error while pausing schedule',
  //     )
  //     handleOnClosing()
  //   },
  // })

  const handleOnClick = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    let properties = {}

    if (fields.radar || fields.comment || blockReason || fields.blockedUrls) {
      properties = {
        radar: fields.radar || undefined,
        comment:
          `${fields.comment} \n Blocked Urls: \n ${fields.blockedUrls}` ||
          undefined,
        reasons: [{ reason: blockReason }],
        createRadar: blockReason === 'Brand Ops Review' ? true : createRadar,
      }
    }

    if (pauseConfig) {
      // scheduleMutation.mutate({ configurationId, isPaused })
      togglePauseConfig()
    }

    mutation.mutate({
      taskIds: [task.id],
      ...properties,
    })
  }

  useEffect(() => {
    if (blockReason === 'Robots not allowed') {
      setCreateRadar(true)
    } else {
      setCreateRadar(false)
    }
  }, [blockReason])

  const urlRegex =
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g

  const isValidUrl =
    blockReason === 'Robots not allowed' &&
    (!fields.blockedUrls.match(urlRegex) ||
      fields.blockedUrls.indexOf('gws') !== -1)

  return (
    <>
      <ThemedDialog
        isOpen={isLoading || isModalOpen}
        title="Block task"
        onClose={toggleModal}
        onClosing={handleOnClosing}
        icon="disable"
      >
        <form onSubmit={handleOnClick}>
          <div className={Classes.DIALOG_BODY}>
            You are about to block this task. If you are sure that this is what
            you want to do, please type in the last five characters of the task
            ID:
            {validator}
            <Box mt={2}>
              <FormGroup
                label="Block reason"
                labelFor="task-block-reason"
                inline
              >
                <HTMLSelect
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                >
                  <option disabled={!!blockReason}>
                    Select a block reason
                  </option>
                  {blockReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </HTMLSelect>
              </FormGroup>
            </Box>
            <Box mt={1}>
              <Label>Comment</Label>
              <TextArea
                required
                fill
                name="comment"
                value={fields.comment}
                rows={2}
                onChange={handleOnFieldChange}
                disabled={!isValidated}
                placeholder="Reason for blocking this task"
              />
            </Box>
            {blockReason === 'Robots not allowed' && (
              <>
                <Box mt={1}>
                  <Label>Blocked URLs</Label>
                  <TextArea
                    required
                    fill
                    name="blockedUrls"
                    value={fields.blockedUrls}
                    rows={2}
                    onChange={handleOnFieldChange}
                    disabled={!isValidated}
                    placeholder="List of blocked urls with a line seperator"
                  />
                </Box>
                {fields.blockedUrls && isValidUrl && (
                  <Text mt={1} color="RED3">
                    Only urls out of gws are allowed
                  </Text>
                )}
              </>
            )}
            <Box mt={2}>
              <Checkbox
                onChange={() => togglePauseConfig()}
                checked={pauseConfig}
                disabled={isPaused}
              >
                Pause the job schedule
              </Checkbox>
            </Box>
            {blockReason && listOfReasonsNeedsRadar.includes(blockReason) && (
              <Box mt={2}>
                <Checkbox
                  onChange={() => setCreateRadar(!createRadar)}
                  checked={createRadar}
                >
                  Create radar
                </Checkbox>
              </Box>
            )}
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button
                onClick={() => {
                  setBlockReason(undefined)
                  toggleModal()
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                intent="primary"
                disabled={
                  !isValidated ||
                  !blockReason ||
                  !fields.comment.trim() ||
                  isValidUrl
                }
                loading={isLoading}
                type="submit"
              >
                Block
              </Button>
            </div>
          </div>
        </form>
      </ThemedDialog>
    </>
  )
}

export default BlockTaskDialog
